(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';
  const media = window.matchMedia(MOBILE_QUERY);
  const runtime = () => window.EvashuRuntime || {};
  const strings = () => runtime().strings || {};
  let moneyFormatter = null;
  let moneyFormatterCurrency = '';
  let moneyFormatterLocale = '';
  let bar = null;
  let originalObserver = null;
  let originalVisibilityObserver = null;
  let footerVisibilityObserver = null;
  let documentObserver = null;
  let cartTotal = 0;
  let selectedVariant = null;
  let originalVisible = true;
  let footerVisible = false;
  let syncQueued = false;
  let subscriptionsBound = false;
  let observedButton = null;

  const parseMoney = (text) => {
    const value = String(text || '').replace(/[^0-9.,-]/g, '');
    if (!value) return 0;
    const dot = value.lastIndexOf('.');
    const comma = value.lastIndexOf(',');
    const decimal = Math.max(dot, comma);
    let normalized;
    if (decimal > -1 && value.length - decimal - 1 === 2) {
      normalized = `${value.slice(0, decimal).replace(/[.,]/g, '')}.${value.slice(decimal + 1)}`;
    } else {
      normalized = value.replace(/[.,]/g, '');
    }
    const amount = Number.parseFloat(normalized);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  };

  const currency = () => window.Shopify?.currency?.active || runtime().currency || 'USD';
  const formatter = () => {
    const activeCurrency = currency();
    const locale = document.documentElement.lang || runtime().locale || 'en-US';
    if (moneyFormatter && moneyFormatterCurrency === activeCurrency && moneyFormatterLocale === locale) return moneyFormatter;
    try {
      moneyFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      moneyFormatterCurrency = activeCurrency;
      moneyFormatterLocale = locale;
      return moneyFormatter;
    } catch (error) {
      moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      moneyFormatterCurrency = 'USD';
      moneyFormatterLocale = 'en-US';
      return moneyFormatter;
    }
  };
  const formatMoney = (cents) => formatter().format((Number(cents) || 0) / 100);
  const token = (template, amount) => String(template || '').replace('{amount}', amount);

  const productRoot = () => document.querySelector('product-info, .section-main-product');
  const originalButton = () => productRoot()?.querySelector('.product-form__submit');
  const quantityInput = () => productRoot()?.querySelector('.product-form__quantity .quantity__input, quantity-input .quantity__input, input[name="quantity"]');

  const readSelectedVariant = () => {
    const script = productRoot()?.querySelector(
      'variant-selects [data-selected-variant], variant-radios [data-selected-variant], [data-selected-variant][type="application/json"]'
    );
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  };

  const readDomPricing = () => {
    const root = productRoot();
    const button = originalButton();
    const priceBox = root?.querySelector('.product__info-container .price, .price');
    const onSale = priceBox?.classList.contains('price--on-sale');
    const regular = priceBox?.querySelector('.price__regular .price-item--regular');
    const sale = priceBox?.querySelector('.price__sale .price-item--sale');
    const compare = priceBox?.querySelector('.price__sale .price-item--regular');
    return {
      price: parseMoney(onSale ? sale?.textContent : regular?.textContent),
      compare_at_price: onSale ? parseMoney(compare?.textContent) : 0,
      available: !(button?.disabled || button?.getAttribute('aria-disabled') === 'true'),
    };
  };

  const currentProductTitle = () =>
    productRoot()?.querySelector('.product__title h1, .product__title')?.textContent.trim() || 'Selected crystal';

  const currentProductImage = () => {
    const image = document.querySelector(
      '.product__media-item.is-active img, .product__media-item[is-active] img, .product__media-item img, media-gallery img'
    );
    return image?.currentSrc || image?.src || '';
  };

  const ensureBar = () => {
    if (bar?.isConnected) return bar;
    bar = document.querySelector('.ev-mobile-atc');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'ev-mobile-atc';
      document.body.appendChild(bar);
    }
    bar.dataset.evMobileAtcV4 = 'true';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', strings().quickAddRegion || 'Quick add to cart');
    bar.innerHTML = `
      <div class="ev-mobile-atc__top">
        <div class="ev-mobile-atc__image-wrap" aria-hidden="true">
          <img class="ev-mobile-atc__image" alt="" loading="lazy">
        </div>
        <div class="ev-mobile-atc__meta">
          <span class="ev-mobile-atc__title"></span>
          <span class="ev-mobile-atc__price-row">
            <span class="ev-mobile-atc__sale"></span>
            <s class="ev-mobile-atc__compare" hidden></s>
            <span class="ev-mobile-atc__discount" hidden></span>
          </span>
        </div>
        <button type="button" class="button ev-mobile-atc__cta"></button>
      </div>
      <div class="ev-mobile-atc__shipping" aria-live="polite">
        <div class="ev-mobile-atc__shipping-copy">
          <span class="ev-mobile-atc__shipping-label"></span>
          <strong class="ev-mobile-atc__shipping-state"></strong>
        </div>
        <div class="ev-mobile-atc__progress" role="progressbar" aria-label="${strings().freeShippingProgress || 'Free shipping progress'}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <span class="ev-mobile-atc__progress-fill"></span>
        </div>
      </div>`;

    bar.querySelector('.ev-mobile-atc__cta').addEventListener('click', () => {
      const button = originalButton();
      if (button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') button.click();
    });
    return bar;
  };

  const updateVisibility = () => {
    if (!bar) return;
    bar.classList.toggle('is-visible', media.matches && !originalVisible && !footerVisible && Boolean(originalButton()));
  };

  const bindVisibility = () => {
    originalVisibilityObserver?.disconnect();
    footerVisibilityObserver?.disconnect();
    originalVisible = true;
    footerVisible = false;

    if (!media.matches) {
      updateVisibility();
      return;
    }

    const button = originalButton();
    const footer = document.querySelector('.eih-footer, footer');
    if (button) {
      originalVisibilityObserver = new IntersectionObserver(([entry]) => {
        originalVisible = entry.isIntersecting;
        updateVisibility();
      }, { threshold: 0.1 });
      originalVisibilityObserver.observe(button);
    }
    if (footer) {
      footerVisibilityObserver = new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        updateVisibility();
      }, { threshold: 0.01 });
      footerVisibilityObserver.observe(footer);
    }
  };

  const updateDesktopShipping = (variant) => {
    const offer = document.querySelector('.evashu-shipping-offer');
    if (!offer) return;
    const title = offer.querySelector('.evashu-shipping-offer__title');
    const helper = offer.querySelector('.evashu-shipping-offer__helper');
    const progress = offer.querySelector('.evashu-shipping-offer__progress');
    const fill = progress?.querySelector('span');
    if (!title || !helper || !progress || !fill) return;

    const config = runtime().freeShipping || {};
    const threshold = Number(config.thresholdCents) || 0;
    const thresholdCurrency = config.currency || 'USD';
    if (!threshold || currency() !== thresholdCurrency) {
      title.textContent = strings().shippingCalculated || 'Shipping calculated at checkout';
      helper.textContent = strings().shippingCalculatedDetail || 'Final shipping options and cost are shown at checkout.';
      progress.hidden = true;
      return;
    }

    progress.hidden = false;
    const current = variant || readSelectedVariant() || readDomPricing();
    const price = Number(current?.price) || readDomPricing().price;
    const quantity = Math.max(1, Number.parseInt(quantityInput()?.value || '1', 10) || 1);
    const projected = Math.max(0, cartTotal + (price * quantity));
    const remaining = Math.max(0, threshold - projected);
    const percent = Math.max(0, Math.min(100, (projected / threshold) * 100));

    title.replaceChildren();
    const strong = document.createElement('strong');
    strong.textContent = config.paidShippingLabel || '$5.99 International Shipping';
    title.append(strong, document.createTextNode(` · ${config.freeShippingLabel || 'FREE Delivery on Orders $49+'}`));
    helper.textContent = remaining > 0
      ? token(strings().shippingAddMore || 'Add {amount} more', formatMoney(remaining))
      : (strings().shippingUnlocked || 'Unlocked ✓');
    fill.style.width = `${percent}%`;
  };

  const updateShipping = (variant) => {
    if (!bar) return;
    const config = runtime().freeShipping || {};
    const threshold = Number(config.thresholdCents) || 0;
    const thresholdCurrency = config.currency || 'USD';
    const label = bar.querySelector('.ev-mobile-atc__shipping-label');
    const state = bar.querySelector('.ev-mobile-atc__shipping-state');
    const progress = bar.querySelector('.ev-mobile-atc__progress');
    const fill = bar.querySelector('.ev-mobile-atc__progress-fill');
    if (!label || !state || !progress || !fill) return;

    if (!threshold || currency() !== thresholdCurrency) {
      label.textContent = strings().shippingCalculated || 'Shipping calculated at checkout';
      state.textContent = '';
      state.classList.remove('is-unlocked');
      progress.hidden = true;
      updateDesktopShipping(variant);
      return;
    }

    progress.hidden = false;
    const current = variant || selectedVariant || readSelectedVariant() || readDomPricing();
    const price = Number(current?.price) || readDomPricing().price;
    const quantity = Math.max(1, Number.parseInt(quantityInput()?.value || '1', 10) || 1);
    const projected = Math.max(0, cartTotal + (price * quantity));
    const remaining = Math.max(0, threshold - projected);
    const percent = Math.max(0, Math.min(100, (projected / threshold) * 100));

    label.textContent = token(strings().shippingAbove || 'Free shipping above {amount}', formatMoney(threshold));
    state.textContent = remaining > 0
      ? token(strings().shippingAddMore || 'Add {amount} more', formatMoney(remaining))
      : (strings().shippingUnlocked || 'Unlocked ✓');
    state.classList.toggle('is-unlocked', remaining === 0);
    fill.style.width = `${percent}%`;
    progress.setAttribute('aria-valuenow', String(Math.round(percent)));
    updateDesktopShipping(current);
  };

  const updateBar = (variant = null) => {
    if (!media.matches || !originalButton()) {
      bar?.classList.remove('is-visible');
      updateDesktopShipping(variant || selectedVariant);
      return;
    }

    ensureBar();
    const current = variant || readSelectedVariant() || readDomPricing();
    selectedVariant = current;
    const fallback = readDomPricing();
    const price = Number(current?.price) || fallback.price;
    const compare = Number(current?.compare_at_price) || fallback.compare_at_price;
    const available = current?.available !== false && fallback.available;
    const title = currentProductTitle();
    const button = originalButton();

    bar.querySelector('.ev-mobile-atc__title').textContent = title;
    const imageWrap = bar.querySelector('.ev-mobile-atc__image-wrap');
    const image = bar.querySelector('.ev-mobile-atc__image');
    const imageSource = current?.featured_media?.preview_image?.src || current?.featured_image?.src || currentProductImage();
    if (imageSource) {
      image.src = imageSource;
      imageWrap.hidden = false;
    } else {
      imageWrap.hidden = true;
    }

    const sale = bar.querySelector('.ev-mobile-atc__sale');
    const compareNode = bar.querySelector('.ev-mobile-atc__compare');
    const discount = bar.querySelector('.ev-mobile-atc__discount');
    sale.textContent = formatMoney(price);
    const onSale = compare > price && price > 0;
    compareNode.hidden = !onSale;
    discount.hidden = !onSale;
    if (onSale) {
      compareNode.textContent = formatMoney(compare);
      discount.textContent = `${Math.round(((compare - price) / compare) * 100)}% OFF`;
    } else {
      compareNode.textContent = '';
      discount.textContent = '';
    }

    const cta = bar.querySelector('.ev-mobile-atc__cta');
    const sourceLabel = button?.querySelector('span')?.textContent.trim() || button?.textContent.trim();
    const label = sourceLabel || (available ? (strings().addToCart || 'Add to cart') : (strings().soldOut || 'Sold out'));
    cta.textContent = label;
    cta.disabled = !available || button?.disabled || button?.getAttribute('aria-disabled') === 'true';
    cta.setAttribute('aria-disabled', String(cta.disabled));
    cta.setAttribute('aria-label', `${label}: ${title}`);
    cta.classList.toggle('is-sold-out', cta.disabled);

    updateShipping(current);
    updateVisibility();
  };

  const scheduleSync = (variant = null, rebind = false) => {
    if (variant) selectedVariant = variant;
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      if (rebind) bindVisibility();
      updateBar(selectedVariant);
    });
  };

  const refreshCart = async () => {
    const root = window.Shopify?.routes?.root || runtime().routes?.root || '/';
    try {
      const response = await fetch(`${root.replace(/\/$/, '')}/cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      cartTotal = Number(cart.total_price) || 0;
    } catch (error) {
      // Keep the latest known total when the cart endpoint is unavailable.
    }
    scheduleSync();
  };

  const bindOriginalObserver = () => {
    originalObserver?.disconnect();
    const button = originalButton();
    observedButton = button || null;
    if (!button) return;
    originalObserver = new MutationObserver(() => scheduleSync());
    originalObserver.observe(button, { attributes: true, childList: true, subtree: true });
  };

  const bindProductObserver = () => {
    documentObserver?.disconnect();
    const root = productRoot();
    if (!root) {
      documentObserver = null;
      return;
    }
    documentObserver = new MutationObserver(() => {
      if (originalButton() !== observedButton) rebindProduct();
    });
    documentObserver.observe(root, { childList: true, subtree: true });
  };

  const rebindProduct = () => {
    selectedVariant = readSelectedVariant() || readDomPricing();
    bindOriginalObserver();
    bindProductObserver();
    bindVisibility();
    scheduleSync(selectedVariant);
  };

  const bindSubscriptions = () => {
    if (subscriptionsBound) return;
    subscriptionsBound = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant || event?.variant || event?.data?.data?.variant;
        scheduleSync(variant || null);
      });
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => window.setTimeout(refreshCart, 80));
    }

    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (event.promise?.then) {
        event.promise.then(({ cart }) => {
          const amount = cart?.cost?.totalAmount?.amount;
          if (amount != null) cartTotal = Math.round(Number(amount) * 100);
          scheduleSync();
        }).catch(() => refreshCart());
      } else {
        refreshCart();
      }
    });
    document.addEventListener('product-info:loaded', () => rebindProduct());
    document.addEventListener('shopify:section:load', () => rebindProduct());
  };

  const init = () => {
    bindSubscriptions();
    if (media.addEventListener) media.addEventListener('change', () => rebindProduct());
    else media.addListener(() => rebindProduct());
    document.addEventListener('input', (event) => {
      if (event.target.matches?.('.product-form__quantity .quantity__input, quantity-input .quantity__input, input[name="quantity"]')) scheduleSync();
    });
    document.addEventListener('change', (event) => {
      if (event.target.matches?.('.product-form__quantity .quantity__input, quantity-input .quantity__input, input[name="quantity"]')) scheduleSync();
    });
    rebindProduct();
    refreshCart();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
